import { createUseStyles } from 'react-jss'
import { THEME } from 'components/theme'

export const MyPlanStyles = createUseStyles({
  root: {
    height: '100%',
    width: '100%',
    overflowY: 'auto',
    // Android planScreenGround: white / #121212
    backgroundColor: THEME.screenBackground,
    boxSizing: 'border-box'
  },
  header: {
    fontSize: 16,
    fontWeight: 'bold',
    color: THEME.brandBlack,
    padding: '20px 32px',
    borderBottom: `1px solid ${THEME.brandGreyPrimary}`
  },
  centerState: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '60%',
    color: THEME.brandDarkerGrey,
    fontSize: 14
  },
  plansRow: {
    display: 'flex',
    justifyContent: 'center',
    gap: 24,
    flexWrap: 'wrap',
    padding: '28px 32px'
  },
  // Plan card (Android SubscriptionPlanCard): grey50 / #1c1c1e plane one step above the
  // ground, no border unless selected, then a 2px primary border
  card: {
    flex: '0 1 360px',
    border: '1px solid transparent',
    borderRadius: 16,
    backgroundColor: THEME.screenCard,
    cursor: 'pointer',
    overflow: 'hidden',
    transition: 'border-color 0.15s, box-shadow 0.15s'
  },
  cardSelected: {
    borderColor: THEME.brandGreen,
    boxShadow: `0 0 0 1px ${THEME.brandGreen}`
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    padding: '16px 18px',
    borderBottom: `1px solid ${THEME.brandGreyPrimary}`
  },
  cardTitleWrap: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4
  },
  cardTitleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8
  },
  cardName: {
    fontSize: 16,
    fontWeight: 'bold',
    color: THEME.brandBlack
  },
  // Android ActiveBadge: solid primary pill, 10sp white label
  activeBadge: {
    fontSize: 10,
    lineHeight: '14px',
    fontWeight: 600,
    color: THEME.onBrand,
    backgroundColor: THEME.brandGreen,
    borderRadius: 10,
    padding: '2px 8px'
  },
  // Android PlanPriceBlock: 12sp label line with the amount as an 18sp bold span, so the
  // price reads as the headline and "for N days" as its caption
  cardPrice: {
    fontSize: 12,
    lineHeight: '24px',
    color: THEME.brandBlack
  },
  cardPriceAmount: {
    fontSize: 18,
    fontWeight: 'bold'
  },
  // Discount line under the price (Android DiscountLabel): struck list price in grey700,
  // "(Save ₹X)" in brand green
  cardDiscountRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    fontSize: 12,
    lineHeight: 1.3
  },
  cardPriceStrike: {
    textDecoration: 'line-through',
    color: THEME.brandDarGrey4
  },
  cardPriceSave: {
    color: THEME.brandGreen,
    fontWeight: 600
  },
  cardExpiry: {
    fontSize: 12,
    color: THEME.brandDarkerGrey,
    marginTop: 2
  },
  // Android PlanSelectionIndicator: a 24px box holding either a filled primary check-circle
  // (selected) or a 20px, 2px-thick grey ring (unselected)
  selectionBox: {
    flexShrink: 0,
    width: 24,
    height: 24,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  selectionRing: {
    width: 20,
    height: 20,
    boxSizing: 'border-box',
    borderRadius: '50%',
    border: `2px solid ${THEME.iconDiscMuted}`
  },
  selectionCheck: {
    width: 24,
    height: 24,
    borderRadius: '50%',
    backgroundColor: THEME.brandGreen,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  benefitList: {
    listStyle: 'none',
    margin: 0,
    padding: '14px 18px',
    display: 'flex',
    flexDirection: 'column',
    gap: 12
  },
  benefitRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    fontSize: 13,
    color: THEME.brandBlackAlt,
    lineHeight: 1.4
  },
  benefitCheck: {
    flexShrink: 0,
    marginTop: 1
  },
  // Solid 36px disc behind each benefit icon (Android BenefitIconDisc: iconDiscMuted, no border)
  benefitIconCircle: {
    flexShrink: 0,
    width: 36,
    height: 36,
    borderRadius: '50%',
    backgroundColor: THEME.iconDiscMuted,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  // 20px glyph tinted in the row ink (Android: onBackground / grey900)
  benefitIcon: {
    width: 20,
    height: 20,
    color: THEME.brandBlack
  },
  mobileStar: {
    color: THEME.brandDarkerGrey
  },
  footnote: {
    fontSize: 11,
    color: THEME.brandDarkerGrey,
    padding: '0 18px 16px'
  },
  // CTA card
  ctaCard: {
    margin: '0 auto 32px',
    border: `1px solid ${THEME.brandGreyPrimary}`,
    borderRadius: 12,
    padding: '16px 20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
    width: '100%',
    maxWidth: 744
  },
  ctaText: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4
  },
  ctaTitle: {
    fontSize: 15,
    fontWeight: 'bold',
    color: THEME.brandBlack
  },
  ctaSubtitle: {
    fontSize: 12,
    color: THEME.brandDarkerGrey
  },
  ctaButton: {
    flexShrink: 0,
    border: 'none',
    cursor: 'pointer',
    backgroundColor: THEME.brandGreen,
    color: THEME.onBrand,
    fontWeight: 600,
    fontSize: 15,
    borderRadius: 24,
    padding: '12px 28px',
    display: 'flex',
    alignItems: 'center'
  },
  // Android: Downgrade is the fixed orange_primary pill with white text; every other mode is
  // the primary button
  ctaButtonDowngrade: {
    backgroundColor: THEME.ctaOrangeStrong,
    color: THEME.onBrand
  },
  // Payment modal
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.45)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 100000
  },
  modal: {
    backgroundColor: THEME.brandWhite,
    borderRadius: 12,
    width: 900,
    maxWidth: '95vw',
    maxHeight: '90vh',
    overflowY: 'auto',
    // Extra top padding so the content (esp. the right heading) clears the Close pill.
    padding: '56px 28px 28px',
    position: 'relative'
  },
  // "× Close" pill, top-right (matches the design / subscription-required page)
  closePill: {
    position: 'absolute',
    top: 16,
    right: 16,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    border: `1px solid ${THEME.brandGreyPrimary}`,
    backgroundColor: THEME.brandWhite,
    borderRadius: 20,
    padding: '6px 14px',
    fontSize: 13,
    lineHeight: 1,
    color: THEME.brandBlack,
    '& svg': {
      display: 'block'
    }
  },
  phonesRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8
  },
  phoneWrap: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 8
  },
  phoneImg: {
    height: 300,
    width: 'auto'
  },
  phoneCaption: {
    fontSize: 13,
    fontWeight: 600,
    color: THEME.brandBlack
  },
  payColumns: {
    display: 'flex',
    alignItems: 'stretch',
    gap: 0
  },
  payCol: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '12px 24px',
    textAlign: 'center'
  },
  payColDivider: {
    width: 1,
    backgroundColor: THEME.brandGreyPrimary,
    position: 'relative'
  },
  payOrBadge: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    backgroundColor: THEME.brandWhite,
    border: `1px solid ${THEME.brandGreyPrimary}`,
    borderRadius: '50%',
    width: 34,
    height: 34,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 12,
    color: THEME.brandDarkerGrey
  },
  payHeading: {
    fontSize: 14,
    fontWeight: 600,
    color: THEME.brandBlack,
    marginBottom: 16,
    display: 'flex',
    alignItems: 'center',
    gap: 6
  },
  qrBox: {
    border: `1px solid ${THEME.brandGreyPrimary}`,
    borderRadius: 8,
    padding: 12,
    backgroundColor: THEME.brandWhite
  },
  payable: {
    marginTop: 14,
    fontSize: 14,
    color: THEME.brandBlack
  },
  appSteps: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    fontSize: 13,
    color: THEME.brandBlackAlt,
    textAlign: 'left',
    marginTop: 8
  },
  payStateText: {
    textAlign: 'center',
    fontSize: 14,
    padding: '32px 0',
    color: THEME.brandDarkerGrey
  },
  paySuccess: {
    color: THEME.brandGreen,
    fontWeight: 600
  },
  // Payment success state
  '@keyframes successPop': {
    '0%': { transform: 'scale(0)', opacity: 0 },
    '60%': { transform: 'scale(1.15)', opacity: 1 },
    '100%': { transform: 'scale(1)', opacity: 1 }
  },
  successWrap: {
    // Anchors the confetti canvas overlay
    position: 'relative',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    padding: '40px 0'
  },
  successCircle: {
    width: 72,
    height: 72,
    borderRadius: '50%',
    backgroundColor: THEME.brandGreen,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    animationName: '$successPop',
    animationDuration: '0.4s',
    animationTimingFunction: 'ease-out'
  },
  successTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: THEME.brandBlack,
    marginTop: 4
  },
  successSubtitle: {
    fontSize: 14,
    color: THEME.brandDarkerGrey,
    textAlign: 'center'
  },
  successBtn: {
    marginTop: 12,
    border: 'none',
    cursor: 'pointer',
    backgroundColor: THEME.brandGreen,
    color: THEME.onBrand,
    fontWeight: 600,
    fontSize: 14,
    borderRadius: 24,
    padding: '10px 32px'
  },
  payFailed: {
    color: THEME.brandRed,
    fontWeight: 600
  }
})
